/**
 * CV Portal Color System - Named Constants Approach
 * 
 * This file provides a centralized color system using named constants instead of magic strings.
 * This approach provides better type safety, IntelliSense support, and maintainability.
 * 
 * USAGE EXAMPLES:
 * 
 * // OLD WAY (magic strings - deprecated)
 * colors.getBrandColor('complimentaryBlue')
 * colors.getGradientValue('homepage')
 * 
 * // NEW WAY (named constants - recommended)
 * colors.brand.COMPLIMENTARY_BLUE
 * colors.gradients.HOMEPAGE
 * colors.tech.TECHNOLOGY
 * colors.status.SUCCESS
 * 
 * AVAILABLE COLOR GROUPS:
 * - colors.brand.*     - Sopra Steria brand colors
 * - colors.ui.*        - UI element colors (borders, backgrounds)
 * - colors.tech.*      - Technology and skill related colors
 * - colors.status.*    - Status colors (success, error, warning, info)
 * - colors.chat.*      - Chat/messaging specific colors
 * - colors.export.*    - Export/action specific colors
 * - colors.gradients.* - Predefined gradients
 * 
 * BENEFITS:
 * ✅ Type safety - no more typos in color names
 * ✅ IntelliSense - autocomplete shows available colors
 * ✅ Refactoring - easy to rename colors across the codebase
 * ✅ Documentation - self-documenting color purposes
 * ✅ Consistency - enforced color usage patterns
 */

import { MantineColor } from '@mantine/core';

// Brand Colors - Sopra Steria Official Colors
export const BRAND_COLORS = {
  // Primary brand colors
  red: '#cf022b',
  orange: '#ef7d00',
  black: '#1d1d1b',
  darkGray: '#4a4a49',
  mediumGray: '#a8a8a7',
  lightGray: '#ededed',

  // Secondary brand colors
  blue: '#007ac2',
  complimentaryBlue: '#5376ad',
  darkBlue: '#1D3A8C',
  lazur: '#32abd0',
  purple: '#8c1d82',
  darkPurple: '#4a1d8c',
  purpleBlue: '#251D8C',
  pink: '#ea5599',
  darkGreen: '#00a188',
  lightGreen: '#95c11f',

  // Extended brand colors
  lightOrange: '#F28A1A',
  darkerOrange: '#E67100',
  darkerRed: '#B80226',
  lightPurple: '#721B6E',
  darkerPurple: '#3D1768',
  darkestPurple: '#2D1150',

  // Utility colors
  white: '#ffffff',
  borderLight: '#e0e0e0',
  backgroundLight: '#f5f5f5',
  backgroundMedium: '#f8f9fa',
  backgroundDark: '#e9ecef',
  backgroundLightPurple: '#f1e8f0',

  // Semantic colors
  errorRed: '#dc3545',
  teamBlue: '#2563EB',
} as const;

// Named color constants for easy access
export const BrandColor = {
  PRIMARY_RED: BRAND_COLORS.red,
  PRIMARY_ORANGE: BRAND_COLORS.orange,
  PRIMARY_BLACK: BRAND_COLORS.black,
  PRIMARY_BLUE: BRAND_COLORS.blue,

  COMPLIMENTARY_BLUE: BRAND_COLORS.complimentaryBlue,
  DARK_BLUE: BRAND_COLORS.darkBlue,
  LIGHT_GREEN: BRAND_COLORS.lightGreen,
  DARK_GREEN: BRAND_COLORS.darkGreen,

  PURPLE: BRAND_COLORS.purple,
  DARK_PURPLE: BRAND_COLORS.darkPurple,
  LIGHT_PURPLE: BRAND_COLORS.lightPurple,

  LAZUR: BRAND_COLORS.lazur,
  PINK: BRAND_COLORS.pink,

  WHITE: BRAND_COLORS.white,
  BLACK: BRAND_COLORS.black,
  LIGHT_GRAY: BRAND_COLORS.lightGray,
  MEDIUM_GRAY: BRAND_COLORS.mediumGray,
  DARK_GRAY: BRAND_COLORS.darkGray,

  BACKGROUND_LIGHT_PURPLE: BRAND_COLORS.backgroundLightPurple,

  ERROR_RED: BRAND_COLORS.errorRed,
  TEAM_BLUE: BRAND_COLORS.teamBlue,
} as const;

// UI Color constants
export const UIColor = {
  BORDER_LIGHT: BRAND_COLORS.borderLight,
  BACKGROUND_LIGHT: BRAND_COLORS.backgroundLight,
  BACKGROUND_MEDIUM: BRAND_COLORS.backgroundMedium,
  BACKGROUND_DARK: BRAND_COLORS.backgroundDark,
} as const;

// Technology and skill colors
export const TechColor = {
  TECHNOLOGY: BrandColor.COMPLIMENTARY_BLUE,
  FAVORITE_TECHNOLOGY: BrandColor.PRIMARY_BLUE,
  ROLE: BrandColor.LIGHT_GREEN,
  SKILL: BrandColor.PURPLE,
} as const;

// Status colors
export const StatusColor = {
  SUCCESS: BrandColor.LIGHT_GREEN,
  ERROR: BrandColor.ERROR_RED,
  WARNING: BrandColor.PRIMARY_ORANGE,
  INFO: BrandColor.PRIMARY_BLUE,
} as const;

// Chat colors
export const ChatColor = {
  USER_MESSAGE: BrandColor.COMPLIMENTARY_BLUE,
  BOT_MESSAGE: UIColor.BACKGROUND_LIGHT,
  BACKGROUND: UIColor.BACKGROUND_MEDIUM,
} as const;

// Export colors
export const ExportColor = {
  ERROR: BrandColor.ERROR_RED,
  SUCCESS: BrandColor.LIGHT_GREEN,
} as const;

// Form UI Colors - Special colors for form sections
export const FormUIColor = {
  ADD_FORM_BG: '#dff0f0a2', // Desaturated cyan with transparency for "add" form backgrounds
  ADD_FORM_BORDER: '#007ac2', // Cyan-7 equivalent for borders
} as const;

// Gradient constants
export const Gradient = {
  PRIMARY: 'linear-gradient(90deg, #673AB7 0%, #E31B23 35%, #FF9800 100%)',
  PRIMARY_DARK: 'linear-gradient(90deg, #673AB7 0%, #E31B23 75%, #FF9800 100%)',
  HOMEPAGE: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  HOMEPAGE_HOVER: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
  HEADER: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
} as const;

// Brand Color Palettes - Full Material Design inspired palettes for Sopra Steria brand color families
// Each palette covers the full 50–900 shade range for its brand color family,
// making them directly usable in Mantine theme, CSS variables, and component styling.
export const BRAND_PALETTES = {
  // Gray palette
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },

  // Red palette
  red: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
  },

  // Orange palette
  orange: {
    50: '#fff3e0',
    100: '#ffe0b2',
    200: '#ffcc80',
    300: '#ffb74d',
    400: '#ffa726',
    500: '#ff9800',
    600: '#fb8c00',
    700: '#f57c00',
    800: '#ef6c00',
    900: '#e65100',
  },

  // Deep Orange palette
  deepOrange: {
    50: '#fbe9e7',
    100: '#ffccbc',
    200: '#ffab91',
    300: '#ff8a65',
    400: '#ff7043',
    500: '#ff5722',
    600: '#f4511e',
    700: '#e64a19',
    800: '#d84315',
    900: '#bf360c',
  },

  // Amber palette
  amber: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#ffc107',
    600: '#ffb300',
    700: '#ffa000',
    800: '#ff8f00',
    900: '#ff6f00',
  },

  // Light Green palette
  lightGreen: {
    50: '#f1f8e9',
    100: '#dcedc8',
    200: '#c5e1a5',
    300: '#aed581',
    400: '#9ccc65',
    500: '#8bc34a',
    600: '#7cb342',
    700: '#689f38',
    800: '#558b2f',
    900: '#33691e',
  },

  // Green palette
  green: {
    50: '#e8f5e9',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50',
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },

  // Teal palette
  teal: {
    50: '#e0f2f1',
    100: '#b2dfdb',
    200: '#80cbc4',
    300: '#4db6ac',
    400: '#26a69a',
    500: '#009688',
    600: '#00897b',
    700: '#00796b',
    800: '#00695c',
    900: '#004d40',
  },

  // Cyan palette
  cyan: {
    50: '#e0f7fa',
    100: '#b2ebf2',
    200: '#80deea',
    300: '#4dd0e1',
    400: '#26c6da',
    500: '#00bcd4',
    600: '#00acc1',
    700: '#0097a7',
    800: '#00838f',
    900: '#006064',
  },

  // Purple palette
  purple: {
    50: '#f3e5f5',
    100: '#e1bee7',
    200: '#ce93d8',
    300: '#ba68c8',
    400: '#ab47bc',
    500: '#9c27b0',
    600: '#8e24aa',
    700: '#7b1fa2',
    800: '#6a1b9a',
    900: '#4a148c',
  },

  // Deep Purple palette
  deepPurple: {
    50: '#ede7f6',
    100: '#d1c4e9',
    200: '#b39ddb',
    300: '#9575cd',
    400: '#7e57c2',
    500: '#673ab7',
    600: '#5e35b1',
    700: '#512da8',
    800: '#4527a0',
    900: '#311b92',
  },

  // Blue palette (Material Design inspired)
  blue: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3',
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },
} as const;

// Backward-compatible alias — prefer BRAND_PALETTES in new code
/** @deprecated Use BRAND_PALETTES instead */
export const DESIGN_PALETTES = BRAND_PALETTES;

// Gradient definitions found in the app
export const GRADIENTS = {
  primary: 'linear-gradient(90deg, #673AB7 0%, #E31B23 35%, #FF9800 100%)',
  primaryDark: 'linear-gradient(90deg, #673AB7 0%, #E31B23 75%, #FF9800 100%)',
  homepage: 'linear-gradient(90deg, #673AB7 0%, #E31B23 50%, #FF9800 100%)',
  homepageHover: 'linear-gradient(90deg, #7e51cf 0%, #e73e46 50%, #ffad33 100%)',
  header: 'linear-gradient(to right, #8b5cf6, #ef4444, #eab308)',
} as const;

// Semantic color mappings for different use cases
export const SEMANTIC_COLORS = {
  // Status colors
  success: BrandColor.LIGHT_GREEN,
  warning: BrandColor.PRIMARY_ORANGE,
  error: BrandColor.ERROR_RED,
  info: BrandColor.PRIMARY_BLUE,

  // UI element colors
  primary: BrandColor.PRIMARY_RED,
  secondary: BrandColor.COMPLIMENTARY_BLUE,
  accent: BrandColor.PRIMARY_ORANGE,

  // Text colors
  textPrimary: BrandColor.BLACK,
  textSecondary: BrandColor.DARK_GRAY,
  textMuted: BrandColor.MEDIUM_GRAY,

  // Background colors
  backgroundLight: UIColor.BACKGROUND_LIGHT,
  backgroundGray: BrandColor.LIGHT_GRAY,
  backgroundDark: UIColor.BACKGROUND_DARK,
  backgroundMedium: UIColor.BACKGROUND_MEDIUM,

  // Border colors
  borderLight: UIColor.BORDER_LIGHT,
  borderMedium: BrandColor.MEDIUM_GRAY,
  borderDark: BrandColor.DARK_GRAY,

  // Chat/Message colors
  chatBackground: ChatColor.BACKGROUND,
  chatBackgroundDark: BrandColor.DARK_GRAY,

  // Export colors
  exportError: ExportColor.ERROR,
} as const;

// CV Section specific colors using brand palette
export const CV_SECTION_PALETTE = {
  'personal-details': { color: BrandColor.PRIMARY_BLUE, mantineColor: 'blue' as MantineColor },
  'summary-qualifications': { color: BrandColor.LAZUR, mantineColor: 'cyan' as MantineColor },
  'project-experience': { color: BrandColor.DARK_BLUE, mantineColor: 'indigo' as MantineColor },
  'roles-overview': { color: BrandColor.PURPLE, mantineColor: 'violet' as MantineColor },
  'skills-overview': { color: BrandColor.DARK_PURPLE, mantineColor: 'grape' as MantineColor },
  'education': { color: BrandColor.PINK, mantineColor: 'pink' as MantineColor },
  'work-experience': { color: BrandColor.PRIMARY_ORANGE, mantineColor: 'orange' as MantineColor },
  'certifications': { color: BrandColor.LIGHT_GREEN, mantineColor: 'lime' as MantineColor },
  'languages': { color: BrandColor.DARK_GREEN, mantineColor: 'green' as MantineColor },
  'training-courses': { color: BrandColor.COMPLIMENTARY_BLUE, mantineColor: 'blue' as MantineColor },
} as const;

// Export types for TypeScript support
export type BrandColorKey = keyof typeof BRAND_COLORS;
export type SemanticColorKey = keyof typeof SEMANTIC_COLORS;
export type CVSectionKey = keyof typeof CV_SECTION_PALETTE;
export type BrandPaletteKey = keyof typeof BRAND_PALETTES;
/** @deprecated Use BrandPaletteKey instead */
export type DesignPaletteKey = BrandPaletteKey;
export type GradientKey = keyof typeof GRADIENTS;

// Utility functions for color usage
export const getColorValue = (colorKey: BrandColorKey): string => {
  return BRAND_COLORS[colorKey];
};

export const getSemanticColor = (semanticKey: SemanticColorKey): string => {
  return SEMANTIC_COLORS[semanticKey];
};

export const getCVSectionColor = (sectionKey: CVSectionKey) => {
  return CV_SECTION_PALETTE[sectionKey];
};

export const getBrandPalette = (paletteKey: BrandPaletteKey) => {
  return BRAND_PALETTES[paletteKey];
};

/** @deprecated Use getBrandPalette instead */
export const getDesignPalette = getBrandPalette;

export const getGradient = (gradientKey: GradientKey): string => {
  return GRADIENTS[gradientKey];
};

// Get specific shade from design palette
export const getPaletteColor = (paletteKey: BrandPaletteKey, shade: keyof typeof BRAND_PALETTES.gray): string => {
  return BRAND_PALETTES[paletteKey][shade];
};

// Color variations generator (for creating lighter/darker versions)
export const generateColorVariations = (baseColor: string) => {
  // This is a simple implementation - you might want to use a color manipulation library
  // like polished or color2k for more sophisticated color operations
  return {
    lighter: baseColor + '20', // Add alpha for lighter version
    light: baseColor + '40',
    base: baseColor,
    dark: baseColor + 'CC',
    darker: baseColor + '80',
  };
};

// Predefined color combinations for consistent theming
export const COLOR_COMBINATIONS = {
  primary: {
    background: SEMANTIC_COLORS.backgroundLight,
    text: SEMANTIC_COLORS.textPrimary,
    accent: SEMANTIC_COLORS.primary,
  },
  secondary: {
    background: SEMANTIC_COLORS.backgroundGray,
    text: SEMANTIC_COLORS.textSecondary,
    accent: SEMANTIC_COLORS.secondary,
  },
  dark: {
    background: SEMANTIC_COLORS.backgroundDark,
    text: '#ffffff',
    accent: SEMANTIC_COLORS.accent,
  },
  chat: {
    background: SEMANTIC_COLORS.chatBackground,
    backgroundDark: SEMANTIC_COLORS.chatBackgroundDark,
    text: SEMANTIC_COLORS.textPrimary,
  },
} as const;

export type ColorCombinationKey = keyof typeof COLOR_COMBINATIONS; 